/**
 * Dispatch Analysis Component
 * Tabbed layout: Summary | Weekly | Per Dispatcher
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../utils/api';
import { ComposedChart, BarChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, Legend, PieChart, Pie, Cell } from 'recharts';
import { CHART_COLORS, SERIES_PALETTE, getDhBarColor } from '../utils/chartTheme';
import KpiCards from './KpiCards';
import AlertsBanner from './AlertsBanner';
import '../styles/DispatchAnalysis.css';
import '../styles/Charts.css';

function DispatchAnalysis({ selectedCompany = '', buildParams: parentBuildParams, filterOptions = { dispatchers: [], brokers: [], drivers: [] }, selectedWeeks = [], filterVersion = 0 }) {
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [activeSection, setActiveSection] = useState('summary');
  const [exporting, setExporting] = useState(false);

  // Entity-specific filters (kept local — not shared)
  const [dispatcherFilter, setDispatcherFilter] = useState('');
  const [brokerFilter, setBrokerFilter] = useState('');
  const [driverFilter, setDriverFilter] = useState('');

  // Sorting
  const [sortKey, setSortKey] = useState('total_pay');
  const [sortAsc, setSortAsc] = useState(false);

  // Sorting
  const [summarySort, setSummarySort] = useState({ key: 'total_pay', dir: 'desc' });
  const [detailSort, setDetailSort] = useState({ key: null, dir: 'desc' });

  // Detail loads
  const [detailLoads, setDetailLoads] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPage, setDetailPage] = useState(1);

  // KPI data for charts
  const [kpiData, setKpiData] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(false);

  // Track if initial load done for this company
  const [loadedCompany, setLoadedCompany] = useState(null);

  useEffect(() => {
    if (selectedCompany && selectedCompany !== loadedCompany) {
      setLoadedCompany(selectedCompany);
      setDispatcherFilter('');
      setBrokerFilter('');
      setDriverFilter('');
      loadDispatchSummary();
      loadKpiSummary();
      if (activeSection === 'details') loadDetailLoads();
    }
  }, [selectedCompany]);

  // Re-fetch when shared filters change (filterVersion bumped by Dashboard)
  useEffect(() => {
    if (selectedCompany && loadedCompany && filterVersion > 0) {
      loadDispatchSummary();
      loadKpiSummary();
      if (activeSection === 'details') loadDetailLoads();
    }
  }, [filterVersion]);

  // Real-time fetch on entity dropdown filter change
  useEffect(() => {
    if (selectedCompany && loadedCompany) {
      loadDispatchSummary();
      loadKpiSummary();
      if (activeSection === 'details') loadDetailLoads();
    }
  }, [dispatcherFilter, brokerFilter, driverFilter]);

  useEffect(() => {
    if (selectedCompany && activeSection === 'details') {
      loadDetailLoads();
    }
  }, [detailPage, detailSort]);

  const buildParams = useCallback(() => {
    const extra = {};
    if (dispatcherFilter) extra.dispatcher = dispatcherFilter;
    if (brokerFilter) extra.broker = brokerFilter;
    if (driverFilter) extra.driver_name = driverFilter;
    return parentBuildParams(extra);
  }, [parentBuildParams, dispatcherFilter, brokerFilter, driverFilter]);

  const loadDispatchSummary = async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const qs = buildParams();
      const url = `/loads/${selectedCompany}/dispatch-summary${qs ? '?' + qs : ''}`;
      const response = await api.get(url);
      setSummaryData(response.data);
    } catch (error) {
      console.error('Failed to load dispatch summary:', error);
      setSummaryData(null);
    } finally {
      setLoading(false);
    }
  };

  const loadKpiSummary = async () => {
    if (!selectedCompany) return;
    setKpiLoading(true);
    try {
      const qs = buildParams();
      const url = `/loads/${selectedCompany}/kpi-summary${qs ? '?' + qs : ''}`;
      const response = await api.get(url);
      setKpiData(response.data);
    } catch (error) {
      console.error('Failed to load KPI summary:', error);
    } finally {
      setKpiLoading(false);
    }
  };

  const loadDetailLoads = async () => {
    if (!selectedCompany) return;
    setDetailLoading(true);
    try {
      const qs = buildParams();
      const sep = qs ? '&' : '';
      let sortQs = '';
      if (detailSort.key) {
        sortQs = `&sort_by=${detailSort.key}&sort_dir=${detailSort.dir}`;
      }
      const url = `/loads/${selectedCompany}?page=${detailPage}&limit=50${sep}${qs}${sortQs}`;
      const response = await api.get(url);
      setDetailLoads(response.data);
    } catch (error) {
      console.error('Failed to load detail:', error);
      setDetailLoads(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleClearEntityFilters = () => {
    setDispatcherFilter('');
    setBrokerFilter('');
    setDriverFilter('');
    setDetailPage(1);
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(key === 'dispatcher');
    }
  };

  // --- Sorting helpers ---
  const toggleSort = (setter) => (key) => {
    setter(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  const toggleDetailSort = (key) => {
    setDetailSort(prev => {
      const newDir = prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc';
      return { key, dir: newDir };
    });
    setDetailPage(1);
  };

  const sortIndicator = (sortState, key) => {
    if (sortState.key !== key) return <span className="da-sort-icon da-sort-inactive">{'\u2195'}</span>;
    return <span className="da-sort-icon">{sortState.dir === 'desc' ? '\u25BC' : '\u25B2'}</span>;
  };

  const clientSort = (arr, sortState) => {
    if (!sortState.key || !arr?.length) return arr || [];
    const sorted = [...arr];
    sorted.sort((a, b) => {
      let va = a[sortState.key], vb = b[sortState.key];
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va < vb) return sortState.dir === 'asc' ? -1 : 1;
      if (va > vb) return sortState.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  };

  const sortedSummary = useMemo(() => clientSort(summaryData?.summary, summarySort), [summaryData?.summary, summarySort]);

  const hasData = summaryData?.summary?.length > 0;

  // Filter weekly_trends by selected weeks from Dashboard
  const filteredTrends = useMemo(() => {
    if (!summaryData?.weekly_trends) return [];
    if (!selectedWeeks.length) return summaryData.weekly_trends;
    return summaryData.weekly_trends.filter(t => selectedWeeks.includes(t.week_start));
  }, [summaryData, selectedWeeks]);

  const handleSectionChange = (section) => {
    setActiveSection(section);
    if (section === 'details' && !detailLoads) {
      loadDetailLoads();
    }
  };

  // --- Formatters ---
  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val || 0);
  };

  const formatNumber = (val, decimals = 0) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(val || 0);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    const yy = String(dt.getFullYear()).slice(-2);
    return `${mm}/${dd}/${yy}`;
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();

      if (summaryData?.summary) {
        const summaryRows = summaryData.summary.map(d => ({
          'Dispatcher': d.dispatcher, 'Load Count': d.load_count, 'Total Pay': d.total_pay,
          'Total Miles': d.total_miles, 'Empty Miles': d.total_empty_miles, 'RPM': d.rpm, 'Deadhead %': d.deadhead_pct,
        }));
        if (summaryData.totals) {
          summaryRows.push({
            'Dispatcher': 'TOTAL', 'Load Count': summaryData.totals.load_count, 'Total Pay': summaryData.totals.total_pay,
            'Total Miles': summaryData.totals.total_miles, 'Empty Miles': summaryData.totals.total_empty_miles,
            'RPM': summaryData.totals.rpm, 'Deadhead %': summaryData.totals.deadhead_pct,
          });
        }
        const ws = XLSX.utils.json_to_sheet(summaryRows);
        ws['!cols'] = [{ wch: 20 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 8 }, { wch: 12 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Dispatch Summary');
      }

      if (filteredTrends.length > 0) {
        const trendRows = filteredTrends.map(t => ({
          'Dispatcher': t.dispatcher, 'Week Start': t.week_start, 'Load Count': t.load_count,
          'Total Pay': t.total_pay, 'Total Miles': t.total_miles, 'RPM': t.rpm, 'Deadhead %': t.deadhead_pct,
        }));
        const ws2 = XLSX.utils.json_to_sheet(trendRows);
        ws2['!cols'] = [{ wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 8 }, { wch: 12 }];
        XLSX.utils.book_append_sheet(wb, ws2, 'Weekly Trends');
      }

      XLSX.writeFile(wb, `dispatch_analysis_${selectedCompany}.xlsx`);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // --- Weekly helpers ---
  const formatWeekLabel = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatWeekRange = (dateStr) => {
    const start = new Date(dateStr + 'T00:00:00');
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const s = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const e = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${s} - ${e}`;
  };

  const getRpmClass = (rpm) => {
    if (rpm >= 3.0) return 'rpm-good';
    if (rpm >= 2.0) return 'rpm-ok';
    if (rpm >= 1.5) return 'rpm-low';
    return 'rpm-bad';
  };

  const getHeatClass = (pay, maxPay) => {
    if (!maxPay || !pay) return '';
    const ratio = pay / maxPay;
    if (ratio >= 0.8) return 'heat-green-strong';
    if (ratio >= 0.5) return 'heat-green';
    if (ratio >= 0.3) return 'heat-blue';
    if (ratio >= 0.15) return 'heat-yellow';
    return 'heat-red';
  };

  const getBarFillClass = (rpm) => {
    if (rpm >= 3.0) return 'fill-green';
    if (rpm >= 2.0) return 'fill-blue';
    if (rpm >= 1.5) return 'fill-yellow';
    return 'fill-red';
  };

  const getDhClass = (dh) => {
    if (dh <= 10) return 'dh-low';
    if (dh <= 15) return 'dh-med';
    return 'dh-high';
  };

  const getSortedSummary = () => {
    if (!summaryData?.summary) return [];
    const arr = [...summaryData.summary];
    arr.sort((a, b) => {
      let aVal, bVal;
      if (sortKey === 'dispatcher') {
        aVal = a.dispatcher.toLowerCase();
        bVal = b.dispatcher.toLowerCase();
      } else if (sortKey === 'avg_per_load') {
        aVal = a.load_count > 0 ? a.total_pay / a.load_count : 0;
        bVal = b.load_count > 0 ? b.total_pay / b.load_count : 0;
      } else {
        aVal = a[sortKey] ?? 0;
        bVal = b[sortKey] ?? 0;
      }
      if (aVal < bVal) return sortAsc ? -1 : 1;
      if (aVal > bVal) return sortAsc ? 1 : -1;
      return 0;
    });
    return arr;
  };

  const getWeeklyGrid = () => {
    if (!filteredTrends.length) return null;
    const weeks = [...new Set(filteredTrends.map(t => t.week_start))].sort();
    const dispatchers = [...new Set(filteredTrends.map(t => t.dispatcher))];
    const lookup = {};
    filteredTrends.forEach(t => { lookup[`${t.dispatcher}|${t.week_start}`] = t; });

    const maxPayPerWeek = {};
    weeks.forEach(w => {
      let max = 0;
      dispatchers.forEach(d => {
        const cell = lookup[`${d}|${w}`];
        if (cell && cell.total_pay > max) max = cell.total_pay;
      });
      maxPayPerWeek[w] = max;
    });

    const globalMaxPay = Math.max(...filteredTrends.map(t => t.total_pay || 0), 1);

    const weekTotals = {};
    weeks.forEach(w => {
      let totalPay = 0, totalLoads = 0, totalMiles = 0;
      dispatchers.forEach(d => {
        const cell = lookup[`${d}|${w}`];
        if (cell) {
          totalPay += cell.total_pay || 0;
          totalLoads += cell.load_count || 0;
          totalMiles += cell.total_miles || 0;
        }
      });
      weekTotals[w] = { total_pay: totalPay, load_count: totalLoads, total_miles: totalMiles, rpm: totalMiles > 0 ? totalPay / totalMiles : 0 };
    });

    const dispatcherTotals = {};
    dispatchers.forEach(d => {
      let totalPay = 0, totalLoads = 0, totalMiles = 0;
      weeks.forEach(w => {
        const cell = lookup[`${d}|${w}`];
        if (cell) {
          totalPay += cell.total_pay || 0;
          totalLoads += cell.load_count || 0;
          totalMiles += cell.total_miles || 0;
        }
      });
      dispatcherTotals[d] = { total_pay: totalPay, load_count: totalLoads, rpm: totalMiles > 0 ? totalPay / totalMiles : 0 };
    });

    const grandTotal = { total_pay: 0, load_count: 0, total_miles: 0, rpm: 0 };
    Object.values(weekTotals).forEach(wt => {
      grandTotal.total_pay += wt.total_pay;
      grandTotal.load_count += wt.load_count;
      grandTotal.total_miles += wt.total_miles;
    });
    grandTotal.rpm = grandTotal.total_miles > 0 ? grandTotal.total_pay / grandTotal.total_miles : 0;

    return { weeks, dispatchers, lookup, maxPayPerWeek, globalMaxPay, weekTotals, dispatcherTotals, grandTotal };
  };


  // --- Cross-filter handler (called from charts) ---
  const handleChartBrokerClick = (brokerName) => {
    setBrokerFilter(brokerName);
  };

  // --- Sort header components ---
  const SortTh = ({ sort, onSort, sortKey, children, className = '' }) => (
    <th className={`da-sortable ${className}`} onClick={() => onSort(sortKey)}>
      {children} {sortIndicator(sort, sortKey)}
    </th>
  );

  const DetailSortTh = ({ sortKey, children, className = '' }) => (
    <th className={`da-sortable ${className}`} onClick={() => toggleDetailSort(sortKey)}>
      {children} {sortIndicator(detailSort, sortKey)}
    </th>
  );

  return (
    <div className="dispatch-analysis">
      {/* Entity Filters */}
      <div className="da-controls">
        <div className="da-filters">
          <div className="da-filter-group">
            <label>Dispatcher:</label>
            <select value={dispatcherFilter} onChange={e => setDispatcherFilter(e.target.value)} className="da-filter-select">
              <option value="">All Dispatchers</option>
              {filterOptions.dispatchers.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="da-filter-group">
            <label>Broker:</label>
            <select value={brokerFilter} onChange={e => setBrokerFilter(e.target.value)} className="da-filter-select">
              <option value="">All Brokers</option>
              {filterOptions.brokers.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div className="da-filter-group">
            <label>Driver:</label>
            <select value={driverFilter} onChange={e => setDriverFilter(e.target.value)} className="da-filter-select">
              <option value="">All Drivers</option>
              {filterOptions.drivers.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          {(dispatcherFilter || brokerFilter || driverFilter) && (
            <button onClick={handleClearEntityFilters} className="btn-secondary btn-small">Clear Filters</button>
          )}
        </div>
      </div>

      {/* Section Tabs */}
      <div className="da-tabs">
        <button className={`da-tab ${activeSection === 'summary' ? 'active' : ''}`} onClick={() => handleSectionChange('summary')}>
          Summary
        </button>
        <button className={`da-tab ${activeSection === 'weekly' ? 'active' : ''}`} onClick={() => handleSectionChange('weekly')}>
          Per Dispatcher
        </button>
        <button className={`da-tab ${activeSection === 'details' ? 'active' : ''}`} onClick={() => handleSectionChange('details')}>
          Load Details
        </button>
        <button className={`da-tab ${activeSection === 'alerts' ? 'active' : ''}`} onClick={() => handleSectionChange('alerts')}>
          Alerts
        </button>
        {summaryData?.summary?.length > 0 && (
          <button onClick={handleExport} className="btn-secondary btn-small da-export-btn" disabled={exporting}>
            {exporting ? 'Exporting...' : 'Export Excel'}
          </button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="da-loading">
          <div className="dashboard-spinner"></div>
          <p>Loading dispatch data...</p>
        </div>
      ) : !selectedCompany ? (
        <div className="da-empty">Select a company to view dispatch analysis.</div>
      ) : !hasData ? (
        <div className="da-empty">No dispatch data found. Upload load data in the Admin panel.</div>
      ) : (
        <>
          {/* Summary Table + KPI Charts */}
          {activeSection === 'summary' && (
            !summaryData?.summary?.length ? (
              <div className="da-empty">No dispatch data found. Upload load data in the Admin panel.</div>
            ) : (
              <>
                {/* KPI Cards */}
                {kpiData && (
                  <KpiCards kpis={[
                    { label: 'Total Revenue', value: formatCurrency(kpiData.total_revenue.value), change_pct: kpiData.total_revenue.change_pct },
                    { label: 'Total Loads', value: formatNumber(kpiData.total_loads.value), change_pct: kpiData.total_loads.change_pct },
                    { label: 'RPM (Loaded)', value: '$' + formatNumber(kpiData.avg_rpm_loaded.value, 2), change_pct: kpiData.avg_rpm_loaded.change_pct },
                    { label: 'RPM (All-in)', value: '$' + formatNumber(kpiData.avg_rpm_all_in.value, 2), change_pct: kpiData.avg_rpm_all_in.change_pct },
                    { label: 'Avg Load Pay', value: formatCurrency(kpiData.avg_load_pay.value), change_pct: kpiData.avg_load_pay.change_pct },
                    { label: 'Deadhead %', value: formatNumber(kpiData.deadhead_pct.value, 1) + '%', change_pct: kpiData.deadhead_pct.change_pct ? -kpiData.deadhead_pct.change_pct : null },
                    { label: 'Avg Miles/Load', value: formatNumber(kpiData.avg_miles_per_load.value), change_pct: kpiData.avg_miles_per_load.change_pct },
                  ]} />
                )}

                {/* Dispatcher Summary Table */}
                <div className="da-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <SortTh sort={summarySort} onSort={toggleSort(setSummarySort)} sortKey="dispatcher">Dispatcher</SortTh>
                        <SortTh sort={summarySort} onSort={toggleSort(setSummarySort)} sortKey="load_count" className="text-right">Loads</SortTh>
                        <SortTh sort={summarySort} onSort={toggleSort(setSummarySort)} sortKey="total_pay" className="text-right">Total Pay</SortTh>
                        <SortTh sort={summarySort} onSort={toggleSort(setSummarySort)} sortKey="total_miles" className="text-right">Total Miles</SortTh>
                        <SortTh sort={summarySort} onSort={toggleSort(setSummarySort)} sortKey="total_empty_miles" className="text-right">Empty Mi</SortTh>
                        <SortTh sort={summarySort} onSort={toggleSort(setSummarySort)} sortKey="rpm" className="text-right">RPM</SortTh>
                        <SortTh sort={summarySort} onSort={toggleSort(setSummarySort)} sortKey="deadhead_pct" className="text-right">DH %</SortTh>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedSummary.map((d, i) => (
                        <tr key={i}>
                          <td className="da-dispatcher-name">{d.dispatcher}</td>
                          <td className="text-right">{formatNumber(d.load_count)}</td>
                          <td className="text-right">{formatCurrency(d.total_pay)}</td>
                          <td className="text-right">{formatNumber(d.total_miles)}</td>
                          <td className="text-right">{formatNumber(d.total_empty_miles)}</td>
                          <td className="text-right da-rpm">${formatNumber(d.rpm, 2)}</td>
                          <td className="text-right">
                            <span className={`da-deadhead ${d.deadhead_pct > 15 ? 'high' : d.deadhead_pct > 10 ? 'medium' : 'low'}`}>
                              {formatNumber(d.deadhead_pct, 1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                      {summaryData.totals && (
                        <tr className="da-totals-row">
                          <td className="da-dispatcher-name">TOTAL</td>
                          <td className="text-right">{formatNumber(summaryData.totals.load_count)}</td>
                          <td className="text-right">{formatCurrency(summaryData.totals.total_pay)}</td>
                          <td className="text-right">{formatNumber(summaryData.totals.total_miles)}</td>
                          <td className="text-right">{formatNumber(summaryData.totals.total_empty_miles)}</td>
                          <td className="text-right da-rpm">${formatNumber(summaryData.totals.rpm, 2)}</td>
                          <td className="text-right">{formatNumber(summaryData.totals.deadhead_pct, 1)}%</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Charts Section */}
                {kpiData && (
                  <div className="chart-section">
                    <div className="chart-grid">
                      {/* Revenue & RPM Trend */}
                      <div className="chart-wrapper">
                        <div className="chart-title">Revenue &amp; RPM Trend</div>
                        <ResponsiveContainer width="100%" height={280}>
                          <ComposedChart data={kpiData.weekly_revenue} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                            <XAxis dataKey="week_start" tickFormatter={formatWeekLabel} tick={{ fontSize: 10 }} />
                            <YAxis yAxisId="left" tickFormatter={v => '$' + (v / 1000).toFixed(0) + 'K'} tick={{ fontSize: 10 }} />
                            <YAxis yAxisId="right" orientation="right" tickFormatter={v => '$' + v.toFixed(2)} tick={{ fontSize: 10 }} domain={['auto', 'auto']} />
                            <Tooltip formatter={(v, name) => [name === 'Revenue' ? formatCurrency(v) : '$' + Number(v).toFixed(2), name]} labelFormatter={formatWeekLabel} />
                            <Legend />
                            <Bar dataKey="revenue" yAxisId="left" fill="rgba(37,99,235,0.15)" name="Revenue" radius={[2, 2, 0, 0]}
                              label={({ x, y, width, value }) => (
                                <text x={x + width / 2} y={y - 5} textAnchor="middle" fontSize={8} fontWeight={600} fill="#333">{formatCurrency(value)}</text>
                              )}
                            />
                            <Line dataKey="rpm" yAxisId="right" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} name="RPM"
                              label={({ x, y, value }) => (
                                <text x={x} y={y - 8} textAnchor="middle" fontSize={8} fontWeight={700} fill="#16a34a">${Number(value).toFixed(2)}</text>
                              )}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>

                      {/* RPM Trend */}
                      <div className="chart-wrapper">
                        <div className="chart-title">RPM Trend (Loaded vs All-in)</div>
                        <ResponsiveContainer width="100%" height={280}>
                          <ComposedChart data={kpiData.weekly_rpm} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                            <XAxis dataKey="week_start" tickFormatter={formatWeekLabel} tick={{ fontSize: 10 }} />
                            <YAxis tickFormatter={v => '$' + v.toFixed(2)} tick={{ fontSize: 10 }} domain={['auto', 'auto']} />
                            <Tooltip formatter={(v) => ['$' + Number(v).toFixed(2)]} labelFormatter={formatWeekLabel} />
                            <Legend />
                            <ReferenceLine y={2.0} stroke={CHART_COLORS.negative} strokeDasharray="5 5" label={{ value: '$2.00', position: 'right', fontSize: 10, fill: CHART_COLORS.negative }} />
                            <Line dataKey="rpm_loaded" stroke={CHART_COLORS.info} strokeWidth={2} dot={{ r: 3 }} name="Loaded RPM"
                              label={({ x, y, value }) => (
                                <text x={x} y={y - 8} textAnchor="middle" fontSize={8} fontWeight={700} fill={CHART_COLORS.info}>${Number(value).toFixed(2)}</text>
                              )}
                            />
                            <Line dataKey="rpm_all_in" stroke={CHART_COLORS.warning} strokeWidth={2} dot={{ r: 3 }} name="All-in RPM"
                              label={({ x, y, value }) => (
                                <text x={x} y={y + 14} textAnchor="middle" fontSize={8} fontWeight={700} fill={CHART_COLORS.warning}>${Number(value).toFixed(2)}</text>
                              )}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Deadhead % Trend */}
                      <div className="chart-wrapper">
                        <div className="chart-title">Weekly Deadhead %</div>
                        <ResponsiveContainer width="100%" height={280}>
                          <BarChart data={kpiData.weekly_deadhead} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                            <XAxis dataKey="week_start" tickFormatter={formatWeekLabel} tick={{ fontSize: 10 }} />
                            <YAxis tickFormatter={v => v + '%'} tick={{ fontSize: 10 }} />
                            <Tooltip formatter={(v) => [v.toFixed(1) + '%', 'Deadhead']} labelFormatter={formatWeekLabel} />
                            <Bar dataKey="deadhead_pct" radius={[3, 3, 0, 0]}
                              label={({ x, y, width, value }) => (
                                <text x={x + width / 2} y={y - 5} textAnchor="middle" fontSize={9} fontWeight={600} fill="#333">{Number(value).toFixed(1)}%</text>
                              )}
                            >
                              {kpiData.weekly_deadhead.map((entry, idx) => (
                                <Cell key={idx} fill={getDhBarColor(entry.deadhead_pct)} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Broker Revenue Donut */}
                      <div className="chart-wrapper">
                        <div className="chart-title">Revenue by Broker (Top 10)</div>
                        <ResponsiveContainer width="100%" height={280}>
                          <PieChart>
                            <Pie
                              data={kpiData.broker_distribution}
                              dataKey="revenue" nameKey="broker"
                              cx="50%" cy="50%"
                              innerRadius="50%" outerRadius="80%"
                              paddingAngle={1}
                              onClick={(data) => { if (data?.broker && data.broker !== 'Others') handleChartBrokerClick(data.broker); }}
                              style={{ cursor: 'pointer' }}
                            >
                              {kpiData.broker_distribution.map((entry, idx) => (
                                <Cell key={idx} fill={entry.broker === 'Others' ? '#D1D5DB' : SERIES_PALETTE[idx % SERIES_PALETTE.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v) => [formatCurrency(v)]} />
                            <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: '0.65rem' }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Day of Week Heatmap */}
                    {kpiData.day_of_week_heatmap?.length > 0 && (
                      <div className="chart-wrapper">
                        <div className="chart-title">Load Volume by Day of Week</div>
                        <div className="dow-heatmap">
                          {kpiData.day_of_week_heatmap.map((d, i) => {
                            const maxLoads = Math.max(...kpiData.day_of_week_heatmap.map(x => x.load_count));
                            const intensity = maxLoads > 0 ? d.load_count / maxLoads : 0;
                            const bg = `rgba(37, 99, 235, ${0.05 + intensity * 0.25})`;
                            return (
                              <div key={i} className="dow-cell" style={{ background: bg }}>
                                <div className="dow-cell-label">{d.day_name}</div>
                                <div className="dow-cell-value">{d.load_count}</div>
                                <div className="dow-cell-sub">{formatCurrency(d.revenue)}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Dispatcher Comparison Charts */}
                    {summaryData.summary.length > 1 && (
                      <div className="chart-grid">
                        <div className="chart-wrapper">
                          <div className="chart-title">Revenue per Load by Dispatcher</div>
                          <ResponsiveContainer width="100%" height={Math.max(200, summaryData.summary.length * 28)}>
                            <BarChart layout="vertical" data={summaryData.summary.map(d => ({ ...d, avg_pay: d.load_count > 0 ? Math.round(d.total_pay / d.load_count) : 0 })).sort((a, b) => b.avg_pay - a.avg_pay)} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                              <XAxis type="number" tickFormatter={v => '$' + (v / 1000).toFixed(1) + 'K'} tick={{ fontSize: 10 }} />
                              <YAxis dataKey="dispatcher" type="category" width={110} tick={{ fontSize: 10 }} />
                              <Tooltip formatter={(v) => [formatCurrency(v), 'Avg Pay/Load']} />
                              <Bar dataKey="avg_pay" fill={CHART_COLORS.info} radius={[0, 3, 3, 0]}
                                label={({ x, y, width, height, value }) => (
                                  <text x={x + width + 4} y={y + height / 2 + 4} fontSize={9} fontWeight={600} fill="#333">{formatCurrency(value)}</text>
                                )}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>

                        <div className="chart-wrapper">
                          <div className="chart-title">Deadhead % by Dispatcher</div>
                          <ResponsiveContainer width="100%" height={Math.max(200, summaryData.summary.length * 28)}>
                            <BarChart layout="vertical" data={[...summaryData.summary].sort((a, b) => b.deadhead_pct - a.deadhead_pct)} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                              <XAxis type="number" tickFormatter={v => v + '%'} tick={{ fontSize: 10 }} />
                              <YAxis dataKey="dispatcher" type="category" width={110} tick={{ fontSize: 10 }} />
                              <Tooltip formatter={(v) => [v.toFixed(1) + '%', 'Deadhead']} />
                              <Bar dataKey="deadhead_pct" radius={[0, 3, 3, 0]}
                                label={({ x, y, width, height, value }) => (
                                  <text x={x + width + 4} y={y + height / 2 + 4} fontSize={9} fontWeight={600} fill="#333">{Number(value).toFixed(1)}%</text>
                                )}
                              >
                                {[...summaryData.summary].sort((a, b) => b.deadhead_pct - a.deadhead_pct).map((d, idx) => (
                                  <Cell key={idx} fill={getDhBarColor(d.deadhead_pct)} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )
          )}

          {/* Per Dispatcher Weekly Charts */}
          {activeSection === 'weekly' && (() => {
            const grid = getWeeklyGrid();
            if (!grid) return <div className="da-empty">No weekly trend data available.</div>;

            // Build per-week comparison data (all dispatchers aggregated per week)
            const weeklyComparison = grid.weeks.map(w => {
              const wt = grid.weekTotals[w];
              return {
                week: formatWeekLabel(w),
                weekRange: formatWeekRange(w),
                total_pay: wt.total_pay,
                load_count: wt.load_count,
                rpm: wt.rpm,
              };
            });

            return (
              <div className="da-per-dispatcher">
                {/* Per Week — each week shows all dispatchers side by side */}
                <div className="da-section-header" style={{ textAlign: 'center', fontSize: '1rem', color: '#c0392b', marginBottom: 12 }}>Per week</div>
                <div className="da-chart-grid" style={{ marginBottom: 24 }}>
                  {grid.weeks.map(w => {
                    const weekDispatchers = grid.dispatchers
                      .map(d => {
                        const cell = grid.lookup[`${d}|${w}`];
                        if (!cell) return null;
                        const trucks = cell.truck_count || 1;
                        return { ...cell, dispatcher: d, avg_gross_per_truck: cell.total_pay / trucks };
                      })
                      .filter(Boolean)
                      .sort((a, b) => (b.avg_gross_per_truck || 0) - (a.avg_gross_per_truck || 0));
                    if (!weekDispatchers.length) return null;
                    const wt = grid.weekTotals[w];
                    const weekNum = (() => {
                      const d = new Date(w + 'T00:00:00');
                      const oneJan = new Date(d.getFullYear(), 0, 1);
                      return Math.ceil(((d - oneJan) / 86400000 + oneJan.getDay() + 1) / 7);
                    })();
                    return (
                      <div key={w} className="da-chart-card">
                        <div className="da-chart-card-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                          <span className="da-chart-card-name" style={{ color: '#1a4d8f', fontSize: '0.85rem' }}>W{weekNum} ({formatWeekRange(w)})</span>
                          <span style={{ fontSize: '0.62rem', color: '#c0392b', fontStyle: 'italic' }}>Avg. Gross per Truck and RPM by Dispatcher</span>
                        </div>
                        <div className="da-chart-card-body">
                          <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={weekDispatchers} margin={{ top: 32, right: 10, left: 10, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                              <XAxis dataKey="dispatcher" tick={{ fontSize: 8 }} interval={0} angle={-20} textAnchor="end" height={45} />
                              <YAxis tickFormatter={v => '$' + (v / 1000).toFixed(0) + 'K'} tick={{ fontSize: 9 }} width={45} />
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (!active || !payload?.length) return null;
                                  const d = payload[0].payload;
                                  return (
                                    <div className="da-chart-tooltip">
                                      <div className="da-chart-tooltip-week">{d.dispatcher}</div>
                                      <div>Avg/Truck: {formatCurrency(d.avg_gross_per_truck)}</div>
                                      <div>Total Rev: {formatCurrency(d.total_pay)}</div>
                                      <div>Trucks: {d.truck_count || 1}</div>
                                      <div>Loads: {d.load_count}</div>
                                      <div>RPM: ${formatNumber(d.rpm, 2)}</div>
                                      <div>Deadhead: {formatNumber(d.deadhead_pct, 1)}%</div>
                                    </div>
                                  );
                                }}
                              />
                              <Bar dataKey="avg_gross_per_truck" fill="#1a2850" radius={[3, 3, 0, 0]}
                                label={({ x, y, width, value, index }) => {
                                  const d = weekDispatchers[index];
                                  const rpmColor = d.rpm >= 3.0 ? '#16a34a' : d.rpm >= 2.0 ? '#d97706' : '#dc2626';
                                  return (
                                    <g>
                                      <rect x={x + width / 2 - 16} y={y - 30} width={32} height={13} rx={3} fill={rpmColor} opacity={0.15} />
                                      <text x={x + width / 2} y={y - 20} textAnchor="middle" fontSize={8} fontWeight={700} fill={rpmColor}>
                                        ${formatNumber(d.rpm, 2)}
                                      </text>
                                      <text x={x + width / 2} y={y - 10} textAnchor="middle" fontSize={7} fontWeight={700} fill="#c0392b">
                                        {formatNumber(d.deadhead_pct, 0)}%
                                      </text>
                                      <text x={x + width / 2} y={y - 1} textAnchor="middle" fontSize={8} fontWeight={600} fill="#333">
                                        {formatCurrency(value)}
                                      </text>
                                    </g>
                                  );
                                }}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Per Dispatcher cards */}
                <div className="da-section-header" style={{ textAlign: 'center', fontSize: '1rem', color: '#c0392b', marginBottom: 16 }}>Per Dispatcher</div>
                <div className="da-chart-grid">
                  {grid.dispatchers.map(disp => {
                    const weekData = grid.weeks.map(w => {
                      const cell = grid.lookup[`${disp}|${w}`];
                      const trucks = cell?.truck_count || 1;
                      return {
                        week: formatWeekLabel(w),
                        weekRange: formatWeekRange(w),
                        total_pay: cell?.total_pay || 0,
                        avg_gross_per_truck: (cell?.total_pay || 0) / trucks,
                        load_count: cell?.load_count || 0,
                        deadhead_pct: cell?.deadhead_pct || 0,
                        rpm: cell?.rpm || 0,
                        truck_count: cell?.truck_count || 0,
                      };
                    });
                    const dt = grid.dispatcherTotals[disp];
                    // Find truck count from summaryData
                    const dispSummary = summaryData?.summary?.find(s => s.dispatcher === disp);
                    const truckCount = dispSummary?.truck_count || 0;
                    return (
                      <div key={disp} className="da-chart-card">
                        <div className="da-chart-card-header">
                          <span className="da-chart-card-name">{disp}</span>
                          <div className="da-chart-card-stats">
                            <span>{formatCurrency(dt.total_pay)}</span>
                            <span style={{ color: 'var(--info)', fontSize: '0.68rem' }}>${formatNumber(dt.rpm, 2)} RPM</span>
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.62rem' }}>{dt.load_count} loads</span>
                            {truckCount > 0 && <span style={{ color: 'var(--text-muted)', fontSize: '0.62rem' }}>{truckCount} trucks</span>}
                          </div>
                        </div>
                        <div className="da-chart-card-body">
                          <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={weekData} margin={{ top: 32, right: 10, left: 10, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                              <XAxis dataKey="weekRange" tick={{ fontSize: 9 }} interval={0} angle={-20} textAnchor="end" height={50} />
                              <YAxis tickFormatter={v => '$' + (v / 1000).toFixed(0) + 'K'} tick={{ fontSize: 9 }} width={45} />
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (!active || !payload?.length) return null;
                                  const d = payload[0].payload;
                                  return (
                                    <div className="da-chart-tooltip">
                                      <div className="da-chart-tooltip-week">{d.weekRange}</div>
                                      <div>Avg/Truck: {formatCurrency(d.avg_gross_per_truck)}</div>
                                      <div>Total Rev: {formatCurrency(d.total_pay)}</div>
                                      <div>Trucks: {d.truck_count}</div>
                                      <div>Loads: {d.load_count}</div>
                                      <div>RPM: ${formatNumber(d.rpm, 2)}</div>
                                      <div>Deadhead: {formatNumber(d.deadhead_pct, 1)}%</div>
                                    </div>
                                  );
                                }}
                              />
                              <Bar dataKey="avg_gross_per_truck" fill="#1a2850" radius={[3, 3, 0, 0]}
                                label={({ x, y, width, value, index }) => {
                                  const d = weekData[index];
                                  const rpmColor = d.rpm >= 3.0 ? '#16a34a' : d.rpm >= 2.0 ? '#d97706' : '#dc2626';
                                  return (
                                    <g>
                                      <rect x={x + width / 2 - 16} y={y - 30} width={32} height={13} rx={3} fill={rpmColor} opacity={0.15} />
                                      <text x={x + width / 2} y={y - 20} textAnchor="middle" fontSize={8} fontWeight={700} fill={rpmColor}>
                                        ${formatNumber(d.rpm, 2)}
                                      </text>
                                      <text x={x + width / 2} y={y - 10} textAnchor="middle" fontSize={8} fontWeight={600} fill="#8b5cf6">
                                        {d.truck_count}T
                                      </text>
                                      <text x={x + width / 2} y={y - 1} textAnchor="middle" fontSize={8} fontWeight={600} fill="#333">
                                        {formatCurrency(value)}
                                      </text>
                                    </g>
                                  );
                                }}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Dispatcher Weekly Matrix */}
                <div className="da-section-header" style={{ textAlign: 'center', fontSize: '1rem', color: '#c0392b', marginBottom: 12 }}>Dispatcher Matrix</div>
                <div className="da-weekly-grid" style={{ marginBottom: 24 }}>
                  <div className="da-table-scroll">
                    <table className="da-weekly-table">
                      <thead>
                        <tr>
                          <th className="da-sticky-col">Dispatcher</th>
                          {grid.weeks.map(w => {
                            const weekNum = (() => {
                              const d = new Date(w + 'T00:00:00');
                              const oneJan = new Date(d.getFullYear(), 0, 1);
                              return Math.ceil(((d - oneJan) / 86400000 + oneJan.getDay() + 1) / 7);
                            })();
                            return (
                              <th key={w} className="da-week-header">
                                <span className="da-week-label">{formatWeekLabel(w)}</span>
                                <span className="da-week-range">{formatWeekRange(w)}</span>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {grid.dispatchers
                          .sort((a, b) => (grid.dispatcherTotals[b]?.total_pay || 0) - (grid.dispatcherTotals[a]?.total_pay || 0))
                          .map(disp => {
                            const dt = grid.dispatcherTotals[disp];
                            return (
                              <tr key={disp}>
                                <td className="da-sticky-col">
                                  <div className="da-sticky-name">
                                    {disp}
                                    <span className="da-sticky-subtitle">{dt.load_count} loads &middot; {formatCurrency(dt.total_pay)}</span>
                                  </div>
                                </td>
                                {grid.weeks.map((w, wi) => {
                                  const cell = grid.lookup[`${disp}|${w}`];
                                  if (!cell) return (
                                    <td key={w} className="da-wcell-empty">
                                      <span className="da-wcell-dash">&mdash;</span>
                                    </td>
                                  );
                                  const prevWeek = wi > 0 ? grid.lookup[`${disp}|${grid.weeks[wi - 1]}`] : null;
                                  const changePct = prevWeek && prevWeek.total_pay > 0
                                    ? Math.round(((cell.total_pay - prevWeek.total_pay) / prevWeek.total_pay) * 100)
                                    : null;
                                  const heatClass = getHeatClass(cell.total_pay, grid.maxPayPerWeek[w]);
                                  const rpmClass = getRpmClass(cell.rpm);
                                  const dhClass = getDhClass(cell.deadhead_pct);
                                  const barPct = grid.globalMaxPay > 0 ? Math.round((cell.total_pay / grid.globalMaxPay) * 100) : 0;
                                  const barFillClass = getBarFillClass(cell.rpm);
                                  return (
                                    <td key={w} className={`da-wcell ${heatClass}`}>
                                      <div className="da-wcell-inner">
                                        <div className="da-wcell-pay">
                                          {formatCurrency(cell.total_pay)}
                                          {changePct !== null && (
                                            <span className={`da-wcell-trend ${changePct >= 0 ? 'trend-up' : 'trend-down'}`}>
                                              {changePct >= 0 ? '\u25B2' : '\u25BC'}{Math.abs(changePct)}%
                                            </span>
                                          )}
                                        </div>
                                        <div className="da-wcell-loads">
                                          {cell.load_count} loads &middot; {formatNumber(cell.total_miles)} mi
                                        </div>
                                        <div style={{ marginTop: 3 }}>
                                          <span className={`da-wcell-rpm ${rpmClass}`}>${formatNumber(cell.rpm, 2)} RPM</span>
                                          <span className={`da-wcell-dh ${dhClass}`}>{formatNumber(cell.deadhead_pct, 0)}% DH</span>
                                        </div>
                                        <div className="da-wcell-bar">
                                          <div className={`da-wcell-bar-fill ${barFillClass}`} style={{ width: `${barPct}%` }} />
                                        </div>
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        {/* Totals row */}
                        <tr className="da-weekly-totals">
                          <td className="da-sticky-col">
                            <div className="da-sticky-name">TOTAL</div>
                          </td>
                          {grid.weeks.map(w => {
                            const wt = grid.weekTotals[w];
                            return (
                              <td key={w} className="da-wcell">
                                <div className="da-wcell-inner">
                                  <div className="da-wcell-pay">{formatCurrency(wt.total_pay)}</div>
                                  <div className="da-wcell-loads">{wt.load_count} loads &middot; {formatNumber(wt.total_miles)} mi</div>
                                  <div style={{ marginTop: 3 }}>
                                    <span className={`da-wcell-rpm ${getRpmClass(wt.rpm)}`}>${formatNumber(wt.rpm, 2)} RPM</span>
                                  </div>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Detail Loads Table */}
          {activeSection === 'details' && (
            detailLoading ? (
              <div className="da-loading">
                <div className="dashboard-spinner"></div>
                <p>Loading loads...</p>
              </div>
            ) : !detailLoads?.loads?.length ? (
              <div className="da-empty">No loads found for the current filters.</div>
            ) : (
              <>
                <div className="da-detail-count">Showing {detailLoads.loads.length} of {detailLoads.total} loads</div>
                <div className="da-table-wrapper da-table-scroll">
                  <table className="data-table da-detail-table">
                    <thead>
                      <tr>
                        <DetailSortTh sortKey="load_id" className="da-col-loadid">Load ID</DetailSortTh>
                        <DetailSortTh sortKey="broker" className="da-col-broker">Broker</DetailSortTh>
                        <DetailSortTh sortKey="driver_name" className="da-col-driver">Driver</DetailSortTh>
                        <DetailSortTh sortKey="truck" className="da-col-truck">Truck</DetailSortTh>
                        <DetailSortTh sortKey="dispatcher" className="da-col-dispatcher">Disp</DetailSortTh>
                        <th className="da-col-location">PU Location</th>
                        <DetailSortTh sortKey="pickup_state" className="da-col-state">PU St</DetailSortTh>
                        <DetailSortTh sortKey="pickup_date" className="da-col-date">PU Date</DetailSortTh>
                        <th className="da-col-location">DEL Location</th>
                        <DetailSortTh sortKey="delivery_state" className="da-col-state">DEL St</DetailSortTh>
                        <DetailSortTh sortKey="delivery_date" className="da-col-date">DEL Date</DetailSortTh>
                        <DetailSortTh sortKey="load_pay" className="da-col-pay text-right">Pay</DetailSortTh>
                        <DetailSortTh sortKey="loaded_mile" className="da-col-miles text-right">Ld Mi</DetailSortTh>
                        <DetailSortTh sortKey="empty_mile" className="da-col-miles text-right">Mt Mi</DetailSortTh>
                        <DetailSortTh sortKey="total_miles" className="da-col-miles text-right">Tot Mi</DetailSortTh>
                        <DetailSortTh sortKey="rpm" className="da-col-rpm text-right">RPM</DetailSortTh>
                        <DetailSortTh sortKey="deadhead_pct" className="da-col-dh text-right">DH%</DetailSortTh>
                      </tr>
                    </thead>
                    <tbody>
                      {detailLoads.loads.map(load => (
                        <tr key={load.id}>
                          <td className="da-load-id da-cell-truncate">{load.load_id}</td>
                          <td className="da-cell-truncate" title={load.broker || ''}>{load.broker || '-'}</td>
                          <td className="da-cell-truncate" title={load.driver_name || ''}>{load.driver_name || '-'}</td>
                          <td className="da-cell-truncate">{load.truck || '-'}</td>
                          <td className="da-cell-truncate" title={load.dispatcher || ''}>{load.dispatcher || '-'}</td>
                          <td className="da-cell-truncate" title={load.pickup_location || ''}>{load.pickup_location || '-'}</td>
                          <td>{load.pickup_state || '-'}</td>
                          <td>{formatDate(load.pickup_date)}</td>
                          <td className="da-cell-truncate" title={load.delivery_location || ''}>{load.delivery_location || '-'}</td>
                          <td>{load.delivery_state || '-'}</td>
                          <td>{formatDate(load.delivery_date)}</td>
                          <td className="text-right">{formatCurrency(load.load_pay)}</td>
                          <td className="text-right">{formatNumber(load.loaded_mile)}</td>
                          <td className="text-right">{formatNumber(load.empty_mile)}</td>
                          <td className="text-right">{formatNumber(load.total_miles)}</td>
                          <td className="text-right da-rpm">${formatNumber(load.rpm, 2)}</td>
                          <td className="text-right">
                            <span className={`da-deadhead ${load.deadhead_pct > 15 ? 'high' : load.deadhead_pct > 10 ? 'medium' : 'low'}`}>
                              {formatNumber(load.deadhead_pct, 1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {detailLoads.total_pages > 1 && (
                  <div className="da-pagination">
                    <button onClick={() => setDetailPage(p => Math.max(1, p - 1))} disabled={detailPage <= 1} className="btn-secondary btn-small">Prev</button>
                    <span>Page {detailPage} of {detailLoads.total_pages}</span>
                    <button onClick={() => setDetailPage(p => Math.min(detailLoads.total_pages, p + 1))} disabled={detailPage >= detailLoads.total_pages} className="btn-secondary btn-small">Next</button>
                  </div>
                )}
              </>
            )
          )}
        </>
      )}

      {activeSection === 'alerts' && (
        <AlertsBanner selectedCompany={selectedCompany} buildParams={buildParams} />
      )}
    </div>
  );
}

export default DispatchAnalysis;
